/**
 * Section Zones de prospection dans la sidebar
 * Affiche la liste des zones avec possibilité de les afficher/masquer
 * Basé sur la même structure que les Catégories
 */

import React, { useState, useEffect, useCallback } from 'react';
import { ProspectionZone, ZONE_COLOR_HEX } from '../../types/prospectionZone';
import * as prospectionZonesService from '../../services/supabase/prospectionZones.service';
import { useAuth } from '../../context/AuthContext';
import { eventsService } from '../../services/events.service';
import { ListItem } from './ListItem';
import {
  CategoryList
} from './styles';

interface ZonesSectionProps {
  isCollapsed: boolean;
  isOwner: boolean;
  onEditZone?: (zone: ProspectionZone) => void;
  onZoneVisibilityChange?: (zoneId: string, visible: boolean) => void;
}

export const ZonesSection: React.FC<ZonesSectionProps> = ({
  isCollapsed,
  isOwner,
  onEditZone,
  onZoneVisibilityChange
}) => {
  const { userProfile } = useAuth();
  const [zones, setZones] = useState<ProspectionZone[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadZones = useCallback(async () => {
    if (!userProfile?.agency_id) return;

    setIsLoading(true);
    try {
      const data = await prospectionZonesService.getProspectionZones({
        agencyId: userProfile.agency_id,
        activeOnly: false
      });
      console.log('🔄 Zones chargées depuis Supabase:', data.length, 'zones');
      console.log('📋 Détail des zones:', data.map(z => ({ id: z.id, name: z.name, description: z.description })));
      
      // Trier les zones par date décroissante (plus récent en haut)
      const sortedZones = data.sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return dateB - dateA; // Ordre décroissant
      });
      
      setZones(sortedZones);
    } catch (error) {
      console.error('Error loading zones:', error);
    } finally {
      setIsLoading(false);
    }
  }, [userProfile?.agency_id]);

  // Charger les zones au montage et quand l'agence change
  useEffect(() => {
    loadZones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.agency_id]);

  // Écouter l'événement de rechargement des zones
  useEffect(() => {
    const handleReloadZones = () => {
      console.log('🔄 RELOAD_ZONES reçu - Rechargement des zones demandé');
      loadZones();
    };

    const unsubscribe = eventsService.subscribe('RELOAD_ZONES', handleReloadZones);
    
    return () => {
      unsubscribe();
    };
  }, [loadZones]);

  const handleEditZone = useCallback((zone: ProspectionZone) => {
    console.log('✏️ Édition de la zone:', zone.name);
    onEditZone?.(zone);
  }, [onEditZone]);

  const handleDeleteZone = useCallback(async (zoneId: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette zone ?')) {
      return;
    }

    try {
      await prospectionZonesService.deleteProspectionZone(zoneId);
      
      // Mettre à jour l'état local en maintenant le tri décroissant
      setZones(prev => {
        const filtered = prev.filter(z => z.id !== zoneId);
        // Maintenir le tri par date décroissante
        return filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      });
      
      // Émettre un événement pour recharger les zones sur la carte
      eventsService.emit('RELOAD_ZONES');
      
      console.log('🗑️ Zone supprimée:', zoneId);
    } catch (error) {
      console.error('Erreur lors de la suppression de la zone:', error);
    }
  }, []);

  const handleToggleVisibility = useCallback(async (zoneId: string) => {
    try {
      // Trouver la zone actuelle
      const zone = zones.find(z => z.id === zoneId);
      if (!zone) return;
      
      const newVisibility = !zone.isVisible;
      
      // Mettre à jour dans Supabase
      await prospectionZonesService.toggleZoneVisibility(zoneId, newVisibility);
      
      // Mettre à jour l'état local en maintenant le tri décroissant
      setZones(prev => {
        const updated = prev.map(z => 
          z.id === zoneId ? { ...z, isVisible: newVisibility } : z
        );
        // Maintenir le tri par date décroissante
        return updated.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      });
      
      // Notifier le parent du changement
      onZoneVisibilityChange?.(zoneId, newVisibility);
      
      // Émettre un événement pour mettre à jour l'affichage sur la carte
      eventsService.emit('ZONE_VISIBILITY_CHANGED', { 
        zoneId, 
        isVisible: newVisibility
      });
      console.log('👁️ Visibilité zone changée:', zoneId, newVisibility, '- Sauvegardé dans Supabase');
    } catch (error) {
      console.error('Erreur lors du changement de visibilité:', error);
    }
  }, [zones, onZoneVisibilityChange]);


  if (isCollapsed) return null;

  return (
    <>
      <div style={{ margin: 0, position: 'relative' }}>
        <CategoryList>
          {isLoading ? (
            <div style={{ 
              padding: '16px', 
              textAlign: 'center', 
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '13px',
              fontStyle: 'italic'
            }}>
              Chargement des zones...
            </div>
          ) : zones.length === 0 ? (
            <div style={{ 
              padding: '16px', 
              textAlign: 'center', 
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '13px',
              fontStyle: 'italic'
            }}>
              Aucune zone créée
            </div>
          ) : (
            zones.map(zone => {
              const isVisible = zone.isVisible;
              const zoneColor = ZONE_COLOR_HEX[zone.color];
              
              return (
                <ListItem
                  key={zone.id}
                  id={zone.id}
                  title={zone.name}
                  color={zoneColor}
                  isVisible={isVisible}
                  isOwner={isOwner}
                  createdAt={zone.createdAt}
                  onToggleVisibility={handleToggleVisibility}
                  onEdit={handleEditZone}
                  onDelete={handleDeleteZone}
                  onClick={() => {
                    // Émettre l'événement pour ouvrir le popup de la zone
                    eventsService.emit('OPEN_ZONE_POPUP', { zoneId: zone.id });
                  }}
                  itemData={zone}
                />
              );
            })
          )}
        </CategoryList>
      </div>

    </>
  );
};
